import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, BookOpen, Pencil, Clock, MapPin, User } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const DAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];
const empty = { id: null, title: "", ustadz: "", day: "Senin", time: "19:30", location: "", enabled: true };

export default function Kajian() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/kajian");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setOpen(true); };
  const openEdit = (k) => { setForm({ ...k }); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Judul kajian wajib diisi");
    const payload = { title: form.title, ustadz: form.ustadz, day: form.day, time: form.time, location: form.location, enabled: form.enabled };
    if (form.id) await api.put(`/kajian/${form.id}`, payload);
    else await api.post("/kajian", payload);
    toast.success("Jadwal pengajian disimpan & tampil di layar");
    setOpen(false);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/kajian/${id}`);
    toast.success("Jadwal dihapus");
    load();
  };

  return (
    <div className="space-y-8" data-testid="kajian-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-primary">Kegiatan</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Pengajian Rutin</h1>
        </div>
        <Button data-testid="kajian-new-button" onClick={openNew} className="h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold gap-2">
          <Plus className="h-5 w-5" /> Tambah Jadwal
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl py-20 text-center">
          <BookOpen className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">Belum ada jadwal pengajian.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kajian-list">
          {items.map((k) => (
            <div key={k.id} className="bg-card border border-border rounded-xl p-5 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display font-semibold text-lg leading-tight">{k.title}</h3>
                <span className={`shrink-0 text-[10px] uppercase tracking-widest px-2 py-1 rounded ${k.enabled ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                  {k.enabled ? "Aktif" : "Nonaktif"}
                </span>
              </div>
              <div className="text-sm text-muted-foreground space-y-1.5">
                {k.ustadz && <p className="flex items-center gap-2"><User className="h-4 w-4 text-primary" /> {k.ustadz}</p>}
                <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> {k.day} · {k.time}</p>
                {k.location && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> {k.location}</p>}
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="secondary" size="sm" data-testid={`kajian-edit-${k.id}`} onClick={() => openEdit(k)} className="gap-2">
                  <Pencil className="h-4 w-4" /> Ubah
                </Button>
                <Button variant="ghost" size="icon" data-testid={`kajian-delete-${k.id}`} onClick={() => remove(k.id)} className="text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-card border-border max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? "Ubah Jadwal Pengajian" : "Tambah Jadwal Pengajian"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <Label>Judul / Tema</Label>
              <Input data-testid="kajian-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 bg-background" placeholder="mis. Kajian Tafsir Al-Quran" />
            </div>
            <div>
              <Label>Pemateri (Ustadz)</Label>
              <Input data-testid="kajian-ustadz-input" value={form.ustadz} onChange={(e) => setForm({ ...form, ustadz: e.target.value })} className="mt-2 bg-background" placeholder="mis. Ust. Ahmad" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hari</Label>
                <Select value={form.day} onValueChange={(v) => setForm({ ...form, day: v })}>
                  <SelectTrigger className="mt-2 bg-background" data-testid="kajian-day-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Jam</Label>
                <Input type="time" data-testid="kajian-time-input" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-2 bg-background" />
              </div>
            </div>
            <div>
              <Label>Lokasi / Ruang</Label>
              <Input data-testid="kajian-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-2 bg-background" placeholder="mis. Aula Utama" />
            </div>
            <div className="flex items-center gap-3">
              <Label>Tampilkan</Label>
              <Switch checked={form.enabled} data-testid="kajian-enabled-switch" onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="kajian-save-button" onClick={save} className="bg-primary hover:bg-primary/90 text-primary-foreground">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
